import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    // TS - Custom override (TS-577): the header search/bag buttons sit above the
    // drawer backdrop, so their dialogs can open while the drawer is open — close
    // it so overlays don't stack and its scroll lock releases. dialog:open
    // doesn't bubble; listen during capture.
    document.addEventListener('dialog:open', this.#handleDialogOpen, true);
    // TS - Custom override: the measured drawer offset goes stale if the
    // viewport resizes (e.g. rotation) while the drawer is open.
    window.addEventListener('resize', this.#handleResize);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    // TS - Custom override (TS-577)
    document.removeEventListener('dialog:open', this.#handleDialogOpen, true);
    // TS - Custom override
    window.removeEventListener('resize', this.#handleResize);
  }

  /**
   * TS - Custom override: re-pin the open drawer to the header after a resize.
   */
  #handleResize = () => {
    if (this.isOpen) this.#updateDrawerTop();
  };

  /**
   * TS - Custom override (TS-577): close the drawer when any dialog opens over it.
   */
  #handleDialogOpen = () => {
    if (this.isOpen) this.close();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // TS - Custom override: scroll is locked while the drawer is open, so the
    // value measured here can't go stale.
    this.#updateDrawerTop();

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      // TS - Custom override (TS-577): a close (e.g. a dialog opening over the
      // drawer) can land between open() and this frame — re-adding menu-open to
      // a reset drawer would show its backdrop the next time [open] is set.
      if (!details.hasAttribute('open')) return;

      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      // TS - Custom override (TS-577): skip the trap if the drawer was closed in
      // the meantime (e.g. a dialog opened over it) — trapping focus on a closed
      // drawer would steal it from the dialog.
      onAnimationEnd(
        drawer || details,
        () => {
          if (details.hasAttribute('open')) trapFocus(details);
        },
        { subtree: false }
      );
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    // TS - Custom override (TS-577): while [open] lingers, the drawer's backdrop
    // and scroll lock stay engaged — if the animation settle never resolves the
    // page is left frozen. Race it with a timeout past the drawer animation so
    // the reset always runs (it's idempotent, so a double run is harmless).
    let didFinalize = false;
    const finalize = () => {
      if (didFinalize) return;
      didFinalize = true;
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else if (this.isOpen) {
        // TS - Custom override (TS-577): don't re-trap focus if the whole drawer
        // closed while the submenu was animating (e.g. a dialog opened over it).
        trapFocus(this.refs.details);
      }
    };

    onAnimationEnd(drawer || details, finalize, { subtree: false });
    // TS - Custom override (TS-577): --drawer-animation-speed is 0.2s; leave headroom.
    setTimeout(finalize, 400);
  }

  /**
   * TS - Custom override: pin the drawer and its backdrop to the header's real
   * on-screen bottom (tucked 1px under it). The static --header-group-height
   * fallback in ts-header.css includes the announcement bar, which is scrolled
   * away when the sticky header pins mid-page — leaving a gap above the drawer.
   */
  #updateDrawerTop() {
    const header = this.closest('header-component');
    if (!header) return;

    const top = Math.max(0, Math.round(header.getBoundingClientRect().bottom) - 1);
    this.style.setProperty('--menu-drawer-top', `${top}px`);
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
